import type { DrawingDTO } from '@shared/protocol/messages';
import type { App } from '../app/App';
import { drawThumbnail } from '../drawing/DrawingTextures';
import { isBlank } from '../drawing/strokes';
import { h } from './dom';
import { language, t } from '../i18n';

export interface ButtonOpts {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  color?: 'yellow' | 'pink' | 'blue' | 'green' | 'paper';
  small?: boolean;
  disabled?: boolean;
  title?: string;
  /** Shown as a small handwritten tag, e.g. "needs server". */
  badge?: string;
  onClick?: (ev: MouseEvent) => void;
  sfx?: App;
}

/** Sketchy hand-drawn button. */
export function button(label: string | Node, opts: ButtonOpts = {}): HTMLButtonElement {
  const cls = ['sketch-btn', 'paper-scrap', opts.variant ?? 'secondary', opts.color ? `c-${opts.color} paper-scrap--${opts.color}` : '', opts.small ? 'small' : ''].join(' ');
  const btn = h('button', { class: cls, type: 'button', title: opts.title, disabled: opts.disabled }, h('span', { class: 'label' }, label), opts.badge ? h('span', { class: 'btn-badge' }, opts.badge) : null);
  btn.addEventListener('click', (ev) => {
    opts.sfx?.sfx.play('click');
    opts.onClick?.(ev);
  });
  return btn;
}

/** Pencil-shaded progress bar (HP, EXP, budget). */
export function meter(value: number, max: number, kind: 'hp' | 'exp' | 'budget' | 'stat' = 'stat', label?: string): HTMLElement {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return h(
    'div',
    { class: `meter meter-${kind}`, role: 'meter', 'aria-valuenow': String(value), 'aria-valuemin': '0', 'aria-valuemax': String(max), 'aria-label': label ?? kind },
    h('div', { class: 'meter-fill', style: { width: `${pct}%` } }),
    label ? h('span', { class: 'meter-label' }, label) : null,
  );
}

export function setMeter(el: HTMLElement, value: number, max: number, label?: string): void {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  const fill = el.querySelector<HTMLElement>('.meter-fill');
  if (fill) fill.style.width = `${pct}%`;
  el.setAttribute('aria-valuenow', String(value));
  el.setAttribute('aria-valuemax', String(max));
  const lab = el.querySelector('.meter-label');
  if (lab && label !== undefined) lab.textContent = label;
}

/** Thumbnail canvas: player drawing if present, else the atlas doodle. */
export function thumb(app: App, drawing: DrawingDTO | null | undefined, atlasId: string, size = 96): HTMLCanvasElement {
  const c = h('canvas', { class: 'doodle-thumb', width: size * 2, height: size * 2, style: { width: `${size}px`, height: `${size}px` } });
  if (drawing && !isBlank(drawing)) drawThumbnail(c, drawing);
  else app.assets.drawThumb(c, atlasId);
  return c;
}

/** Delivered DOM art (item icon, sticker, logo) as an <img>, or `fallback` while it is still a placeholder. */
export function artImg(app: App, id: string, cls: string, fallback: Node | null = null): Node | null {
  const url = app.assets.url(id);
  return url ? h('img', { class: `art-img ${cls}`, src: url, alt: '', draggable: false, 'aria-hidden': 'true' }) : fallback;
}

export function stickyNote(color: 'yellow' | 'pink' | 'blue' | 'green', ...children: (Node | string | null)[]): HTMLElement {
  return h('div', { class: `sticky-note ${color}` }, ...children);
}

export interface ModalAction {
  label: string;
  variant?: ButtonOpts['variant'];
  color?: ButtonOpts['color'];
  value: string;
}

/** Paper modal that resolves with the chosen action value (or 'close'). */
/** `accept` can keep the dialog open (return false), e.g. until a field is filled in. */
export function modal(title: string, body: Node | string, actions: ModalAction[], opts: { dismissable?: boolean; className?: string; accept?: (value: string) => boolean; onOpen?: (close: (value: string) => void) => void } = {}): Promise<string> {
  const overlay = document.getElementById('overlay-layer')!;
  return new Promise((resolve) => {
    const prevFocus = document.activeElement as HTMLElement | null;
    const close = (value: string): void => {
      wrap.classList.add('leaving');
      window.removeEventListener('keydown', onKey);
      window.setTimeout(() => wrap.remove(), 180);
      prevFocus?.focus?.();
      resolve(value);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && opts.dismissable !== false) close('close');
    };
    const card = h(
      'div',
      { class: `modal-card paper-card ${opts.className ?? ''}`, role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h('h2', { class: 'modal-title' }, title),
      h('div', { class: 'modal-body' }, body),
      h('div', { class: 'modal-actions' }, ...actions.map((a) => button(a.label, { variant: a.variant ?? 'secondary', color: a.color, onClick: () => (opts.accept?.(a.value) ?? true) && close(a.value) }))),
    );
    const wrap = h('div', { class: 'modal-wrap' }, card);
    wrap.addEventListener('pointerdown', (e) => {
      if (e.target === wrap && opts.dismissable !== false) close('close');
    });
    window.addEventListener('keydown', onKey);
    overlay.appendChild(wrap);
    card.querySelector<HTMLElement>('input, button')?.focus();
    opts.onOpen?.(close);
  });
}

export async function confirmBox(title: string, text: string, yes = t('Yes'), no = t('Cancel')): Promise<boolean> {
  const v = await modal(title, h('p', null, text), [
    { label: no, value: 'no', variant: 'ghost' },
    { label: yes, value: 'yes', variant: 'primary', color: 'pink' },
  ]);
  return v === 'yes';
}

export async function promptBox(title: string, label: string, initial = '', maxLength = 24): Promise<string | null> {
  const input = h('input', { class: 'hand-input', value: initial, maxLength, 'aria-label': label });
  const body = h('label', { class: 'field' }, h('span', null, label), input);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') (body.closest('.modal-card')?.querySelector('.sketch-btn.primary') as HTMLButtonElement | null)?.click();
  });
  const v = await modal(title, body, [
    { label: t('Cancel'), value: 'no', variant: 'ghost' },
    { label: t('OK'), value: 'ok', variant: 'primary', color: 'yellow' },
  ]);
  return v === 'ok' ? input.value.trim() : null;
}

export function statChip(label: string, value: number | string, cls = ''): HTMLElement {
  return h('span', { class: `stat-chip ${cls}` }, h('b', null, label), ` ${value}`);
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(language() === 'th' ? 'th-TH' : undefined, { day: 'numeric', month: 'short' });
}
